import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { MessagesProvider } from './context/MessagesContext';
import { useNotificationsSocket } from './hooks/useNotificationsSocket';
import { useEffect } from 'react';
import Home from './pages/Home';
import MapPage from './pages/MapPage';
import AddOffer from './pages/AddOffer';
import OfferDetails from './pages/OfferDetails';
import FavoritesPage from './pages/FavoritesPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
import MessagesPage from './pages/MessagesPage';
import Analytics from './pages/Analytics';
import MaintenancePage from './pages/MaintenancePage';
import MentionsLegales from './pages/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import CGU from './pages/CGU';
import CompetencesPrompt from './components/CompetencesPrompt';
import './App.css';

// Component to protect routes - redirect to login if not logged in
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  // Wait for auth check to complete before making decision
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-100 via-rose-100 to-purple-200">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }
  
  // User not logged in, redirect
  if (!user) {
    console.log('🚫 ProtectedRoute: User not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }
  
  // User is authenticated
  return children;
}

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Component to initialize WebSocket for notifications
function NotificationsSocketProvider() {
  useNotificationsSocket();
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <NotificationsProvider>
          <FavoritesProvider>
            <MessagesProvider>
              <BrowserRouter>
                <ScrollToTop />
                <NotificationsSocketProvider />
                <CompetencesPrompt />
                <Routes>
                  {/* Routes publiques */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Home />} />
                  <Route path="/carte" element={<MapPage />} />
                  
                  {/* Documents légaux - accessibles sans connexion */}
                  <Route path="/mentions-legales" element={<MentionsLegales />} />
                  <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                  <Route path="/cgu" element={<CGU />} />
                  
                  {/* Routes protégées - nécessitent une connexion */}
                  <Route path="/ajouter" element={<ProtectedRoute><AddOffer /></ProtectedRoute>} />
                  <Route path="/offre/:id" element={<OfferDetails />} />
                  <Route path="/favoris" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/profil/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                  <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                  
                  {/* Page de maintenance / catch-all (404) */}
                  <Route path="/maintenance" element={<MaintenancePage />} />
                  <Route path="*" element={<MaintenancePage />} />
                </Routes>
                <Toaster position="top-right" />
              </BrowserRouter>
            </MessagesProvider>
          </FavoritesProvider>
        </NotificationsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;